import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SiteParcelsModal from '../auditPages/auditSubsections/SiteParcelsModal';
import WalterLoader from '../shared/WalterLoader';
import { useAppMetadata } from '../shared/useAppMetadata';
import '../../styles/CbsSites.css';

// Outil « CBS d'un site » (menu Outils) : calculer le CBS de n'importe quel site (camping, EHPAD…
// du territoire) et l'ajouter à la liste partagée entre auditeurs (/cbs_sites, api/cbs_sites.py),
// qui alimente la comparaison de l'annexe CBS du rapport (/cbs_comparison, MCP get_cbs_comparison).
// La liste montre aussi les bâtiments audités au CBS calculé (non supprimables d'ici).

const API = process.env.REACT_APP_API_URL;
const DEFAULT_RADIUS_KM = 30;
const IGN_GEOCODER = 'https://data.geopf.fr/geocodage/search';
const NOMINATIM = 'https://nominatim.openstreetmap.org/search';
// Catégories proposées par la recherche (types OpenStreetMap, catégories de lieux de l'IGN)
const PLACE_CATEGORIES = {
  camp_site: 'Camping', camping: 'Camping', caravan_site: 'Aire de camping-car', hotel: 'Hôtel', 'hôtel': 'Hôtel',
  nursing_home: 'EHPAD', 'maison de retraite': 'EHPAD', hospital: 'Hôpital', 'hôpital': 'Hôpital',
  school: 'École', college: 'Collège', office: 'Bureaux', sports_centre: 'Centre sportif',
};
// Catégorie → type de bâtiment Walter, sur lequel se filtre la comparaison du rapport
const CATEGORY_TYPES = {
  camping: 'tourisme', 'aire de camping-car': 'tourisme', 'hôtel': 'tourisme', 'village vacances': 'tourisme',
  ehpad: 'sante', 'hôpital': 'sante', clinique: 'sante', bureaux: 'bureau',
};

const formatNumber = (value, digits = 0) => (value == null ? '–'
  : Number(value).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits }));
const first = (value) => (Array.isArray(value) ? value[0] : value) || '';
const normalize = (text) => (text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

function distanceKm([lat1, lon1], [lat2, lon2]) {
  const rad = (d) => (d * Math.PI) / 180;
  const h = Math.sin(rad(lat2 - lat1) / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

// Rouge (CBS faible) → vert (CBS élevé)
function cbsColor(cbs) {
  if (cbs == null) return '#9aa0a6';
  if (cbs < 0.2) return '#d7191c';
  if (cbs < 0.35) return '#fdae61';
  if (cbs < 0.5) return '#a6d96a';
  return '#1a9641';
}

// Adresses et lieux nommés de l'IGN, plus OpenStreetMap (qui connaît les campings par leur nom).
async function searchPlaces(query) {
  const q = encodeURIComponent(query);
  const ign = (index) => fetch(`${IGN_GEOCODER}?q=${q}&index=${index}&limit=5`)
    .then((r) => (r.ok ? r.json() : { features: [] }))
    .then((data) => (data.features || []).map(({ properties: p, geometry }) => ({
      source: index === 'poi' ? 'Lieu (IGN)' : 'Adresse',
      name: index === 'poi' ? first(p.name) : '',
      label: index === 'poi' ? first(p.name) : p.label,
      category: index === 'poi' ? PLACE_CATEGORIES[first(p.category)] || '' : '',
      detail: index === 'poi' ? first(p.category) : '',
      address: index === 'poi' ? '' : p.name,
      city: first(p.city), postal_code: first(p.postcode),
      latitude: geometry.coordinates[1], longitude: geometry.coordinates[0],
    })));
  const osm = fetch(`${NOMINATIM}?q=${q}&format=jsonv2&limit=5&countrycodes=fr&addressdetails=1`)
    .then((r) => (r.ok ? r.json() : []))
    .then((list) => list.map((r) => ({
      source: 'OpenStreetMap',
      name: r.name || '',
      label: r.name || r.display_name.split(',')[0],
      category: PLACE_CATEGORIES[r.type] || '',
      detail: '',
      address: [r.address?.house_number, r.address?.road].filter(Boolean).join(' '),
      city: r.address?.city || r.address?.town || r.address?.village || r.address?.municipality || '',
      postal_code: r.address?.postcode || '',
      latitude: Number(r.lat), longitude: Number(r.lon),
    })));
  const settled = await Promise.allSettled([ign('poi'), osm, ign('address')]);
  return settled.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
}

// Étape principale : chercher le site, le nommer, choisir son terrain → calcul et ajout à la liste.
function NewSiteCard({ categories, saving, onOpenTerrain }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [place, setPlace] = useState(null);

  const search = async (event) => {
    event.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      setResults(await searchPlaces(query.trim()));
    } finally {
      setSearching(false);
    }
  };
  const pick = (result) => {
    setPlace({ ...result, name: result.name || '', category: result.category || '' });
    setResults(null);
  };
  const reset = () => { setPlace(null); setQuery(''); setResults(null); };
  const set = (key) => (e) => setPlace((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <section className="cbs-tool-hero">
      <h2>Calculer le CBS d'un site</h2>
      {!place && (
        <>
          <form onSubmit={search} className="cbs-tool-row">
            <input className="cbs-tool-hero-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Nom du site (« Camping Itsas Mendi », « EHPAD Oihana Bayonne ») ou adresse" />
            <button type="submit" className="cbs-tool-btn big" disabled={searching || !query.trim()}>{searching ? 'Recherche…' : 'Chercher'}</button>
          </form>
          {results && !results.length && (
            <p className="cbs-tool-small">
              Rien trouvé : essayer l'adresse ou la commune, ou{' '}
              <button type="button" className="cbs-tool-link" onClick={() => setPlace({ name: query.trim(), category: '', latitude: null, longitude: null })}>
                placer le terrain sur la carte
              </button>.
            </p>
          )}
          {results?.length > 0 && (
            <ul className="cbs-tool-results">
              {results.map((result, index) => (
                <li key={`${result.source}-${index}`}>
                  <button type="button" onClick={() => pick(result)}>
                    <strong>{result.label}</strong>
                    <span>{[result.category || result.detail, result.address, result.postal_code, result.city].filter(Boolean).join(' · ')}</span>
                    <em>{result.source}</em>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {place && (
        <div className="cbs-tool-place">
          <p className="cbs-tool-small">
            {[place.address, place.postal_code, place.city].filter(Boolean).join(' ') || 'Position à choisir sur la carte'}
            {' '}· <button type="button" className="cbs-tool-link" onClick={reset}>autre site</button>
          </p>
          <div className="cbs-tool-row">
            <label><span>Nom du site *</span><input value={place.name} onChange={set('name')} /></label>
            <label><span>Catégorie</span><input value={place.category} onChange={set('category')} list="cbs-tool-categories" placeholder="Camping, EHPAD…" /></label>
          </div>
          <datalist id="cbs-tool-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          <div className="cbs-tool-row">
            <button type="button" className="cbs-tool-btn big" disabled={!place.name.trim() || saving}
              onClick={() => onOpenTerrain(place, reset)}>
              Choisir le terrain et calculer le CBS
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// Modification d'un site ajouté : nom, catégorie, type, notes, terrain ; suppression.
function EditSiteDialog({ site, buildingTypes, categories, saving, onClose, onSave, onTerrain, onDelete }) {
  const [form, setForm] = useState({ name: site.name || '', category: site.category || '', type_building: site.type_building || '', notes: site.notes || '' });
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  return (
    <div className="cbs-tool-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cbs-tool-dialog">
        <header>
          <h2>{site.name}</h2>
          <button type="button" className="cbs-tool-close" onClick={onClose} aria-label="Fermer"><FaTimes /></button>
        </header>
        <p className="cbs-tool-small">
          CBS {formatNumber(site.cbs, 2)} · potentiel {formatNumber(site.cbs_detail?.cbs_potential, 2)} · {site.cbs_detail?.parcels?.length} parcelle(s),
          {' '}{formatNumber(site.cbs_detail?.surface_m2)} m²{site.created_by && <> · ajouté par {site.created_by}</>}
        </p>
        <label><span>Nom du site *</span><input value={form.name} onChange={set('name')} /></label>
        <div className="cbs-tool-row">
          <label><span>Catégorie</span><input value={form.category} onChange={set('category')} list="cbs-tool-categories" /></label>
          <label><span>Type de bâtiment</span>
            <select value={form.type_building} onChange={set('type_building')}>
              <option value="">—</option>
              {buildingTypes.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </label>
        </div>
        <datalist id="cbs-tool-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        <label><span>Notes</span><textarea rows={2} value={form.notes} onChange={set('notes')} /></label>
        <footer>
          <button type="button" className="cbs-tool-btn ghost danger" onClick={onDelete} disabled={saving}>Supprimer</button>
          <span className="cbs-tool-gap" />
          <button type="button" className="cbs-tool-btn ghost" onClick={onTerrain} disabled={saving}>Modifier le terrain</button>
          <button type="button" className="cbs-tool-btn" onClick={() => onSave(form)} disabled={saving || !form.name.trim()}>Enregistrer</button>
        </footer>
      </div>
    </div>
  );
}

export default function CbsSitesPage() {
  const [searchParams] = useSearchParams();
  const { data: buildingTypes } = useAppMetadata('building_types');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');
  const [department, setDepartment] = useState('');
  const [referenceId, setReferenceId] = useState(searchParams.get('building') || '');
  const [radius, setRadius] = useState(DEFAULT_RADIUS_KM);
  const [terrain, setTerrain] = useState(null);  // { site (nouveau ou existant), done }
  const [editing, setEditing] = useState(null);  // site complet (GET /cbs_sites/<id>)
  const [saving, setSaving] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${API}/cbs_sites`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `erreur ${response.status}`);
      setRows(data.sites);
      setError(null);
    } catch (e) {
      setError(`Liste indisponible (${e.message}).`);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unique = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const categories = useMemo(() => unique(['Camping', 'EHPAD', 'Hôtel', ...(rows || []).map((r) => r.category)]), [rows]);
  const departments = useMemo(() => unique((rows || []).map((r) => r.department)), [rows]);
  const buildings = useMemo(() => (rows || []).filter((r) => r.kind === 'building').sort((a, b) => (a.name || '').localeCompare(b.name || '')), [rows]);
  const reference = buildings.find((b) => b.id === referenceId) || null;
  const typeLabel = (value) => buildingTypes.find((opt) => opt.value === value)?.label || '';

  const shown = useMemo(() => {
    const center = reference?.latitude != null ? [reference.latitude, reference.longitude] : null;
    const words = normalize(text).split(/\s+/).filter(Boolean);
    return (rows || [])
      .filter((r) => !category || normalize(r.category) === normalize(category))
      .filter((r) => !department || r.department === department)
      .filter((r) => {
        const haystack = normalize([r.name, r.city, r.postal_code, r.category].join(' '));
        return words.every((word) => haystack.includes(word));
      })
      .map((r) => ({ ...r, distance_km: center && r.latitude != null ? distanceKm(center, [r.latitude, r.longitude]) : null }))
      .filter((r) => !center || (r.distance_km != null && r.distance_km <= radius))
      .sort((a, b) => (b.cbs ?? -1) - (a.cbs ?? -1));
  }, [rows, text, category, department, reference, radius]);

  const send = async (method, path, body, successMessage) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}${path}`, {
        method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.error || `Erreur ${response.status}`);
        return null;
      }
      toast.success(successMessage(data));
      await load();
      return data;
    } catch (e) {
      toast.error('Enregistrement impossible : vérifiez la connexion.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const onTerrainValidated = async (parcelIds) => {
    const { site, done } = terrain;
    if (!site._id) {
      const created = await send('POST', '/cbs_sites', {
        name: site.name, category: site.category, address: site.address, city: site.city, postal_code: site.postal_code,
        latitude: site.latitude, longitude: site.longitude, parcel_ids: parcelIds,
        type_building: CATEGORY_TYPES[(site.category || '').trim().toLowerCase()] || null,
      }, (data) => `${data.name} ajouté à la liste : CBS ${formatNumber(data.cbs, 2)}`);
      if (created) { setLastAdded(created._id); done?.(); }
      return Boolean(created);
    }
    const updated = await send('PUT', `/cbs_sites/${site._id}`, { parcel_ids: parcelIds },
      (data) => `Terrain mis à jour : CBS ${formatNumber(data.cbs, 2)}`);
    if (updated) setEditing(updated);
    return Boolean(updated);
  };

  const openEdit = async (id) => {
    const response = await fetch(`${API}/cbs_sites/${id}`);
    if (!response.ok) { toast.error('Site introuvable.'); return; }
    setEditing(await response.json());
  };

  const removeSite = async (row) => {
    if (!window.confirm(`Supprimer « ${row.name} » de la liste ? Il sera retiré pour tous les auditeurs.`)) return;
    if (await send('DELETE', `/cbs_sites/${row.id || row._id}`, null, () => `${row.name} supprimé de la liste.`)) setEditing(null);
  };

  const saveEdit = async (form) => {
    if (await send('PUT', `/cbs_sites/${editing._id}`, { ...form, type_building: form.type_building || null }, () => 'Site enregistré.')) setEditing(null);
  };

  const filtersActive = text || category || department || referenceId;
  const terrainParcels = useMemo(() => terrain?.site.cbs_detail?.parcels || [], [terrain]);

  return (
    <div className="cbs-tool-page">
      <div className="cbs-tool-header">
        <h1>CBS d'un site</h1>
        <p className="cbs-tool-small">
          Calculez le CBS de n'importe quel site et ajoutez-le à la liste : elle sert à comparer un site audité aux sites similaires
          de son territoire (annexe CBS du rapport). Liste partagée entre auditeurs ; les bâtiments audités au CBS calculé y figurent d'office.
        </p>
      </div>

      <NewSiteCard categories={categories} saving={saving} onOpenTerrain={(place, done) => setTerrain({ site: place, done })} />

      {error && <div className="cbs-tool-error">{error}</div>}

      <section className="cbs-tool-card">
        <div className="cbs-tool-list-head">
          <h2>Sites calculés {rows && <span>({shown.length}{shown.length !== rows.length ? ` sur ${rows.length}` : ''})</span>}</h2>
          {filtersActive && (
            <button type="button" className="cbs-tool-link" onClick={() => { setText(''); setCategory(''); setDepartment(''); setReferenceId(''); }}>
              Effacer les filtres
            </button>
          )}
        </div>
        <div className="cbs-tool-filters">
          <label className="wide"><span>Recherche</span><input value={text} onChange={(e) => setText(e.target.value)} placeholder="Nom, commune…" /></label>
          <label><span>Catégorie</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Toutes</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label><span>Département</span>
            <select value={department} onChange={(e) => setDepartment(e.target.value)}>
              <option value="">Tous</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="wide"><span>Autour du bâtiment audité</span>
            <select value={referenceId} onChange={(e) => setReferenceId(e.target.value)}>
              <option value="">—</option>
              {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}{b.city ? ` (${b.city})` : ''}</option>)}
            </select>
          </label>
          {reference && (
            <label className="narrow"><span>Rayon (km)</span>
              <input type="number" min={1} max={500} value={radius} onChange={(e) => setRadius(Number(e.target.value) || DEFAULT_RADIUS_KM)} />
            </label>
          )}
        </div>

        {!rows && !error && <WalterLoader size={40} />}
        {rows && (
          <div className="cbs-tool-table-wrap">
            <table className="cbs-tool-table">
              <thead>
                <tr>
                  <th>#</th><th>Site</th><th>Catégorie</th><th>Commune</th>
                  {reference && <th className="n">Distance</th>}
                  <th className="n">CBS</th><th className="n">Potentiel</th><th className="n">Surface</th><th>Source</th><th />
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={10} className="cbs-tool-small">{rows.length ? 'Aucun site pour ces filtres.' : 'Aucun site calculé pour l’instant : commencez par la recherche ci-dessus.'}</td></tr>
                )}
                {shown.map((r, index) => (
                  <tr key={`${r.kind}-${r.id}`} className={[r.id === referenceId ? 'reference' : '', r.id === lastAdded ? 'added' : ''].join(' ')}>
                    <td>{index + 1}</td>
                    <td>
                      {r.kind === 'site'
                        ? <button type="button" className="cbs-tool-link" onClick={() => openEdit(r.id)}>{r.name}</button>
                        : <Link to={`/client/${r.client_id}/${r.id}`}>{r.name}</Link>}
                    </td>
                    <td>{r.category || typeLabel(r.type_building)}</td>
                    <td>{[r.postal_code, r.city].filter(Boolean).join(' ') || r.department}</td>
                    {reference && <td className="n">{r.distance_km == null ? '' : `${formatNumber(r.distance_km, 1)} km`}</td>}
                    <td className="n"><span className="cbs-tool-dot" style={{ background: cbsColor(r.cbs) }} />{formatNumber(r.cbs, 2)}</td>
                    <td className="n">{formatNumber(r.cbs_potential, 2)}</td>
                    <td className="n">{formatNumber(r.surface_m2)} m²</td>
                    <td><span className={`cbs-tool-tag ${r.kind}`}>{r.kind === 'building' ? 'Audit' : 'Ajouté'}</span></td>
                    <td className="n">
                      {r.kind === 'site' && (
                        <button type="button" className="cbs-tool-icon" onClick={() => removeSite(r)} disabled={saving}
                          title="Supprimer de la liste" aria-label={`Supprimer ${r.name}`}><FaTimes /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && !terrain && (
        <EditSiteDialog
          site={editing}
          buildingTypes={buildingTypes}
          categories={categories}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
          onTerrain={() => setTerrain({ site: editing })}
          onDelete={() => removeSite(editing)}
        />
      )}

      {terrain && (
        <SiteParcelsModal
          title={`Terrain — ${terrain.site.name}`}
          latitude={terrain.site.latitude}
          longitude={terrain.site.longitude}
          initialParcels={terrainParcels}
          onClose={() => setTerrain(null)}
          onValidate={onTerrainValidated}
        />
      )}
    </div>
  );
}
